import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { AuthSession } from '../Domain/entity/AuthTypes';
import { signIn } from './authActions';
import { resetUserState } from '../../User/Presentation/userSlice';
import { resetProjectState } from '../../Project/Presentation/projectSlice';
import { resetSettlementState } from '../../Settlement/Presentation/settlementSlice';
import { clearAccessToken, getAccessToken, setAccessToken } from '../../../core/api/tokenStorage';
import { logoutAuth } from '../../../core/api/authApiClient';

type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'error';

const AUTH_USER_ID_STORAGE_KEY = 'missionfund.userId';
const KAKAO_MODAL_SHOWN_STORAGE_KEY = 'missionfund.kakaoModalSeenAfterLogin';
const SUPPRESS_POST_SIGNUP_MODALS_KEY = 'missionfund.suppressPostSignupModals';
const SIGNUP_SKIP_ACCOUNT_FIRST_KEY = 'missionfund.signupSkippedAccountFirst';

const safeSetUserId = (userId?: string) => {
  try {
    if (typeof window === 'undefined') return;
    if (!userId) return;
    window.localStorage.setItem(AUTH_USER_ID_STORAGE_KEY, userId);
  } catch {
    // ignore storage failures (private mode, quota, etc.)
  }
};

const safeRemoveUserId = () => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(AUTH_USER_ID_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const safeSetSessionAccessToken = (accessToken?: string) => {
  setAccessToken(accessToken);
};

const safeClearSessionAccessToken = () => {
  clearAccessToken();
};

const safeResetKakaoModalShown = () => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(KAKAO_MODAL_SHOWN_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const safeResetPostSignupModalSuppression = () => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(SUPPRESS_POST_SIGNUP_MODALS_KEY);
    window.localStorage.removeItem(SIGNUP_SKIP_ACCOUNT_FIRST_KEY);
  } catch {
    // ignore
  }
};

export type AuthState = {
  status: AuthStatus;
  session?: AuthSession;
  optimisticSession?: Partial<AuthSession>;
  error?: string;
};

const initialState: AuthState = {
  status: 'idle',
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signOut(state) {
      state.session = undefined;
      state.optimisticSession = undefined;
      state.status = 'idle';
      state.error = undefined;
      safeRemoveUserId();
      safeClearSessionAccessToken();
      safeResetKakaoModalShown();
      safeResetPostSignupModalSuppression();
    },
    restore(state, action: PayloadAction<AuthSession>) {
      state.session = action.payload;
      state.status = 'authenticated';
      state.error = undefined;
    },
    setSession(state, action: PayloadAction<AuthSession>) {
      state.session = action.payload;
      state.status = 'authenticated';
      state.error = undefined;
      state.optimisticSession = undefined;
      safeResetKakaoModalShown();
      safeSetUserId(action.payload?.user?.id);
      safeSetSessionAccessToken(action.payload?.accessToken);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signIn.pending, (state, action) => {
      state.status = 'loading';
      state.error = undefined;
      state.optimisticSession = {
        accessToken: 'pending',
        user: { email: action.meta.arg.user_email },
      };
    });
    builder.addCase(signIn.fulfilled, (state, action: PayloadAction<AuthSession>) => {
      state.session = action.payload;
      state.status = 'authenticated';
      state.error = undefined;
      state.optimisticSession = undefined;
      safeResetKakaoModalShown();
      safeSetUserId(action.payload?.user?.id);
      safeSetSessionAccessToken(action.payload?.accessToken);
    });
    builder.addCase(signIn.rejected, (state, action) => {
      state.status = 'error';
      state.error = action.payload ?? action.error.message;
      state.session = undefined;
      state.optimisticSession = undefined;
      safeRemoveUserId();
      safeClearSessionAccessToken();
    });
  },
});

export const { signOut, setSession } = authSlice.actions;

/** 로그아웃 시 auth + user + project + settlement store 전체 초기화 */
export const signOutAndClear = () => (dispatch: any) => {
  void logoutAuth().catch(() => undefined);
  dispatch(signOut());
  dispatch(resetUserState());
  dispatch(resetProjectState());
  dispatch(resetSettlementState());
};

export const restoreSession = () => (dispatch: any) => {
  if (typeof window === 'undefined') return;
  const userId = window.localStorage.getItem(AUTH_USER_ID_STORAGE_KEY);
  const accessToken = getAccessToken();
  if (userId && accessToken) {
    // 세션이 있다고 가정하고 상태 복구 (실제 토큰 검증 로직이 있다면 추가 필요)
    // 여기서는 간단히 userId만 있어도 authenticated로 간주하거나, 
    // 실제로는 토큰 유효성을 체크하는 API를 호출해야 함.
    // 현재 구조상 userId만으로는 풀 세션을 복구하기 어려우므로,
    // 최소한의 정보로 로그인 상태를 유지시키거나,
    // getUserDetails를 통해 정보를 가져와야 함.
    
    // 임시: userId가 있으면 로그인된 것으로 처리 (실제 프로덕션에서는 토큰 검증 필수)
    dispatch(authSlice.actions.restore({ user: { id: userId, user_id: userId, email: '' }, accessToken }));
    return;
  }

  if (userId && !accessToken) {
    safeRemoveUserId();
  }
};

export default authSlice.reducer;

